"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { DataTable } from '@open-mercato/ui/backend/DataTable'
import { RowActions } from '@open-mercato/ui/backend/RowActions'
import type { FilterDef, FilterValues } from '@open-mercato/ui/backend/FilterOverlay'
import { BooleanIcon } from '@open-mercato/ui/backend/ValueIcons'
import { Button } from '@open-mercato/ui/primitives/button'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { apiCall, readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { useOrganizationScopeVersion } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import {
  buildLocationCapabilityOptions,
  buildLocationTypeOptions,
  buildSupportedModeOptions,
  formatLocationAddress,
  getLocationCapabilityLabels,
  getLocationTypeLabel,
  getSupportedModeLabels,
  normalizeLocationRecord,
  type LocationRecord,
} from '../lib/locationPresentation'

const PAGE_SIZE = 50

type LocationsResponse = {
  items?: Array<Record<string, unknown>>
  total?: number
  totalPages?: number
}

type FeatureCheckResponse = {
  granted?: string[]
  ok?: boolean
}

function toSelectFilterValues(values: FilterValues): Record<string, string> {
  const next: Record<string, string> = {}
  for (const [key, value] of Object.entries(values ?? {})) {
    if (typeof value === 'string' && value.trim().length > 0) next[key] = value
  }
  return next
}

export default function LocationsPage() {
  const t = useT()
  const router = useRouter()
  const scopeVersion = useOrganizationScopeVersion()
  const { confirm, ConfirmDialogElement } = useConfirmDialog()
  const [rows, setRows] = React.useState<LocationRecord[]>([])
  const [page, setPage] = React.useState(1)
  const [total, setTotal] = React.useState(0)
  const [totalPages, setTotalPages] = React.useState(1)
  const [search, setSearch] = React.useState('')
  const [sorting, setSorting] = React.useState<SortingState>([{ id: 'name', desc: false }])
  const [filterValues, setFilterValues] = React.useState<FilterValues>({})
  const [isLoading, setIsLoading] = React.useState(true)
  const [canManage, setCanManage] = React.useState(false)
  const [reloadToken, setReloadToken] = React.useState(0)

  const typeOptions = React.useMemo(() => buildLocationTypeOptions(t), [t])
  const modeOptions = React.useMemo(() => buildSupportedModeOptions(t), [t])
  const capabilityOptions = React.useMemo(() => buildLocationCapabilityOptions(t), [t])

  const filters = React.useMemo<FilterDef[]>(() => [
    {
      id: 'type',
      label: t('locations.filters.type', 'Location type'),
      type: 'select',
      options: typeOptions,
    },
    {
      id: 'supportedMode',
      label: t('locations.filters.supportedMode', 'Supported mode'),
      type: 'select',
      options: modeOptions,
    },
    {
      id: 'capability',
      label: t('locations.filters.capability', 'Capability'),
      type: 'select',
      options: capabilityOptions,
    },
    {
      id: 'isActive',
      label: t('locations.filters.active', 'Active'),
      type: 'select',
      options: [
        { value: 'true', label: t('locations.filters.yes', 'Yes') },
        { value: 'false', label: t('locations.filters.no', 'No') },
      ],
    },
    {
      id: 'slotBookingEnabled',
      label: t('locations.filters.slotBookingEnabled', 'Slot booking'),
      type: 'select',
      options: [
        { value: 'true', label: t('locations.filters.enabled', 'Enabled') },
        { value: 'false', label: t('locations.filters.disabled', 'Disabled') },
      ],
    },
  ], [capabilityOptions, modeOptions, t, typeOptions])

  const columns = React.useMemo<ColumnDef<LocationRecord>[]>(() => [
    {
      accessorKey: 'name',
      header: t('locations.table.name', 'Name'),
      meta: { priority: 1, sticky: true },
      cell: ({ row }) => (
        <div className="flex flex-col">
          <span className="font-medium">{row.original.name}</span>
          <span className="text-xs text-muted-foreground">
            {[row.original.code, row.original.unlocode].filter(Boolean).join(' · ') || '—'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'type',
      header: t('locations.table.type', 'Type'),
      meta: { priority: 2 },
      cell: ({ row }) => getLocationTypeLabel(row.original.type, t),
    },
    {
      accessorKey: 'address',
      header: t('locations.table.address', 'Address'),
      meta: { priority: 3 },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatLocationAddress(row.original.address) || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'supportedModes',
      header: t('locations.table.supportedModes', 'Modes'),
      meta: { priority: 4 },
      cell: ({ row }) => {
        const labels = getSupportedModeLabels(row.original.supportedModes, t)
        return labels.length ? labels.join(', ') : '—'
      },
    },
    {
      accessorKey: 'capabilities',
      header: t('locations.table.capabilities', 'Capabilities'),
      meta: { priority: 5 },
      cell: ({ row }) => {
        const labels = getLocationCapabilityLabels(row.original.capabilities, t)
        return labels.length ? labels.join(', ') : '—'
      },
    },
    {
      accessorKey: 'slotBookingEnabled',
      header: t('locations.table.slotBookingEnabled', 'Slots'),
      meta: { priority: 6 },
      cell: ({ row }) => <BooleanIcon value={row.original.slotBookingEnabled} />,
    },
    {
      accessorKey: 'isActive',
      header: t('locations.table.active', 'Active'),
      meta: { priority: 7 },
      cell: ({ row }) => <BooleanIcon value={row.original.isActive} />,
    },
  ], [t])

  const loadLocations = React.useCallback(async () => {
    setIsLoading(true)
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
      })
      const sort = sorting[0]
      if (sort?.id) {
        params.set('sortField', sort.id)
        params.set('sortDir', sort.desc ? 'desc' : 'asc')
      }
      if (search.trim()) params.set('search', search.trim())
      for (const [key, value] of Object.entries(toSelectFilterValues(filterValues))) {
        params.set(key, value)
      }

      const payload = await readApiResultOrThrow<LocationsResponse>(
        `/api/freight/locations?${params.toString()}`,
        undefined,
        { errorMessage: t('locations.errors.load', 'Failed to load locations.') },
      )
      const items = Array.isArray(payload.items) ? payload.items : []
      setRows(items.map((item) => normalizeLocationRecord(item)))
      setTotal(typeof payload.total === 'number' ? payload.total : items.length)
      setTotalPages(
        typeof payload.totalPages === 'number'
          ? payload.totalPages
          : Math.max(1, Math.ceil(items.length / PAGE_SIZE)),
      )
    } catch (error) {
      console.error('locations.list.load', error)
      flash(t('locations.errors.load', 'Failed to load locations.'), 'error')
    } finally {
      setIsLoading(false)
    }
  }, [filterValues, page, search, sorting, t])

  React.useEffect(() => {
    let cancelled = false
    async function loadPermissions() {
      try {
        const call = await apiCall<FeatureCheckResponse>('/api/auth/feature-check', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ features: ['locations.manage'] }),
        })
        if (cancelled) return
        const granted = Array.isArray(call.result?.granted) ? call.result.granted : []
        setCanManage(call.result?.ok === true || granted.includes('locations.manage'))
      } catch {
        if (!cancelled) setCanManage(false)
      }
    }

    void loadPermissions()
    return () => {
      cancelled = true
    }
  }, [])

  React.useEffect(() => {
    void loadLocations()
  }, [loadLocations, reloadToken, scopeVersion])

  const handleDelete = React.useCallback(async (row: LocationRecord) => {
    const confirmed = await confirm({
      title: t('locations.actions.delete', 'Delete'),
      text: t('locations.actions.deleteConfirm', 'Delete location "{{name}}"?', { name: row.name }),
      variant: 'destructive',
    })
    if (!confirmed) return

    try {
      await deleteCrud('freight/locations', row.id, {
        errorMessage: t('locations.errors.delete', 'Failed to delete location.'),
      })
      flash(t('locations.messages.deleted', 'Location deleted.'), 'success')
      setReloadToken((value) => value + 1)
    } catch (error) {
      console.error('locations.delete', error)
      flash(t('locations.errors.delete', 'Failed to delete location.'), 'error')
    }
  }, [confirm, t])

  return (
    <Page>
      <PageBody>
        <DataTable<LocationRecord>
          title={t('locations.page.title', 'Locations')}
          data={rows}
          columns={columns}
          isLoading={isLoading}
          searchValue={search}
          onSearchChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          searchPlaceholder={t('locations.table.search', 'Search locations...')}
          filters={filters}
          filterValues={filterValues}
          onFiltersApply={(values) => {
            setFilterValues(values)
            setPage(1)
          }}
          onFiltersClear={() => {
            setFilterValues({})
            setPage(1)
          }}
          emptyState={(
            <p className="py-8 text-center text-sm text-muted-foreground">
              {t('locations.table.empty', 'No locations yet.')}
            </p>
          )}
          actions={canManage ? (
            <div className="flex flex-wrap gap-2">
              <Button asChild size="sm" variant="outline">
                <Link href="/backend/locations/bulk">
                  {t('locations.actions.bulkAdd', 'Bulk add')}
                </Link>
              </Button>
              <Button asChild size="sm">
                <Link href="/backend/locations/create">
                  {t('locations.actions.add', 'Add location')}
                </Link>
              </Button>
            </div>
          ) : undefined}
          refreshButton={{
            label: t('locations.actions.refresh', 'Refresh'),
            onRefresh: () => setReloadToken((value) => value + 1),
            isRefreshing: isLoading,
          }}
          sortable
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={{
            page,
            pageSize: PAGE_SIZE,
            total,
            totalPages,
            onPageChange: setPage,
          }}
          rowActions={canManage
            ? (row) => (
              <RowActions
                items={[
                  { id: 'edit', label: t('locations.actions.edit', 'Edit'), href: `/backend/locations/${row.id}/edit` },
                  {
                    id: 'delete',
                    label: t('locations.actions.delete', 'Delete'),
                    destructive: true,
                    onSelect: () => { void handleDelete(row) },
                  },
                ]}
              />
            )
            : undefined}
          onRowClick={canManage ? (row) => router.push(`/backend/locations/${row.id}`) : undefined}
        />
      </PageBody>
      {ConfirmDialogElement}
    </Page>
  )
}
